/**
 * Theme Store (Zustand)
 * Manages dark/light theme state
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  isDark: boolean;
}

interface ThemeActions {
  setTheme: (theme: Theme) => void;
  setIsDark: (isDark: boolean) => void;
}

type ThemeStore = ThemeState & ThemeActions;

const getEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return theme;
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: 'dark',
      isDark: true,

      setTheme: (theme: Theme) => {
        const effectiveTheme = getEffectiveTheme(theme);
        set({ theme, isDark: effectiveTheme === 'dark' });
      },

      setIsDark: (isDark: boolean) => {
        set({ isDark });
      },
    }),
    {
      name: 'falcon-terminal-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const effectiveTheme = getEffectiveTheme(state.theme);
          state.isDark = effectiveTheme === 'dark';
        }
      },
    }
  )
);

/**
 * Hook to use theme with convenient accessors
 */
export const useTheme = () => {
  const theme = useThemeStore((s) => s.theme);
  const isDark = useThemeStore((s) => s.isDark);
  const setTheme = useThemeStore((s) => s.setTheme);

  return {
    theme,
    isDark,
    setTheme,
  };
};
